import React, { FormEvent, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import ReactForm, { FormProps } from "react-bootstrap/Form";
import {
  FormControl,
  FormGroup,
  FormLabel,
  FormText,
  FormCheck,
} from "react-bootstrap";
import styled from "styled-components/macro";

const $FormGroup = styled(FormGroup)`
  margin-bottom: 1rem;
`;

/**
 * Prevent the default page reload behavior when a form is submitted
 */
const Form: React.FC<FormProps> = (props: FormProps) => {
  return (
    <ReactForm
      {...props}
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (props.onSubmit) {
          props.onSubmit(e);
        }
      }}
    />
  );
};

export default Object.assign(Form, {
  Group: $FormGroup,
  Control: FormControl,
  Label: FormLabel,
  Text: FormText,
  Check: FormCheck,
});

type FirstErrorKey = {
  [key: string]: string | FirstErrorKey;
};

/**
 * Recursively iterate over all errors within the Formik error object and
 * returns the first form field id
 */
function getFirstErrorKey(errors: FirstErrorKey, keys: string[] = []): string {
  const firstErrorKey = Object.keys(errors)[0];
  const firstErrorValue = errors[firstErrorKey];

  if (typeof firstErrorValue === "object") {
    return getFirstErrorKey(firstErrorValue, [...keys, firstErrorKey]);
  }
  return [...keys, firstErrorKey].join(".");
}

type FocusFieldErrorProps = {
  children: React.ReactNode;
};

/**
 * Wraps a form and watches the formik context for submissions.  It will then attempt
 * to autofocus the first field with an error.
 */
export function FocusFieldError({ children }: FocusFieldErrorProps) {
  const formik = useFormikContext();

  const [shouldFocus, setShouldFocus] = useState(false);

  useEffect(() => {
    // Enable autofocusing an error field when the form is submitted
    if (formik.submitCount > 0) {
      setShouldFocus(true);
    }
  }, [formik.submitCount]);

  useEffect(() => {
    if (!formik.isValid && shouldFocus && formik.touched) {
      const firstErrorKey = getFirstErrorKey(formik.errors);
      if (global.window.document.getElementsByName(firstErrorKey).length) {
        // Disable autofocusing after the focusing the first field
        setShouldFocus(false);
        global.window.document.getElementsByName(firstErrorKey)[0].focus();
      }
    }
  }, [formik.isValid, formik.errors, formik.touched, shouldFocus]);

  // Workaround since JSX components can't return a ReactNode.
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
